/**
 * Шейдеры для BillboardLoader (Trail Points System)
 * Вынесены в отдельный файл для лучшей читаемости и возможности подсветки синтаксиса GLSL
 */

/**
 * Vertex shader для системы точек billboard
 */
export const billboardTrailVertexShader = /* glsl */`
  uniform float uTime;
  uniform float uRadius;
  uniform float uGlowSize;
  uniform float uGlowLength;
  uniform float uPointScale;
  
  attribute float aIndex;
  attribute float aSide;
  
  varying float vOpacity;
  varying vec3 vColor;
  varying float vNoise;
  varying float vIndex;

  float hash(float n) { return fract(sin(n) * 43758.5453123); }

  void main() {
    vIndex = aIndex;
    float speed = 2.5;
    // Уплотненный шаг для цельности свечения
    float stepSize = 0.015 * uGlowLength;
    float baseAngle = uTime * speed + (aSide * 3.14159265);
    float angle = baseAngle - (aIndex * stepSize);

    // Турбулентность
    float nVal = hash(aIndex + aSide * 10.0 + uTime * 0.1);
    float noiseX = (nVal - 0.5) * 0.2;
    
    vec3 newPosition = vec3(
      cos(angle) * (uRadius + noiseX),
      noiseX * 0.5,
      sin(angle) * (uRadius + noiseX)
    );

    vColor = (aSide < 0.5) ? vec3(0.0, 0.7, 1.0) : vec3(1.0, 0.2, 0.7);
    
    // vNoise для прерывистости ядра
    vNoise = hash(aIndex * 55.5 + floor(uTime * 30.0));
    vOpacity = 1.0 - (aIndex / 120.0);

    vec4 mvPosition = modelViewMatrix * vec4(newPosition, 1.0);
    
    // Размер точки - адаптирован для MapLibre
    // Используем фиксированный размер, умноженный на масштаб
    float size = 35.0 * uGlowSize * (1.0 - (aIndex / 120.0) * 0.5);
    gl_PointSize = size * uPointScale;
    gl_Position = projectionMatrix * mvPosition;
  }
`

/**
 * Fragment shader для системы точек billboard
 */
export const billboardTrailFragmentShader = /* glsl */`
  uniform float uTime;
  
  varying float vOpacity;
  varying vec3 vColor;
  varying float vNoise;
  varying float vIndex;

  void main() {
    float d = distance(gl_PointCoord, vec2(0.5));
    if (d > 0.5) discard;
    
    // Многослойный bloom-эффект
    // Слой 1: Широкая внешняя аура
    float outerGlow = pow(1.0 - d * 2.0, 1.5) * 0.3;
    
    // Слой 2: Средняя аура
    float midGlow = pow(1.0 - d * 2.0, 3.0) * 0.6;
    
    // Слой 3: Внутреннее свечение
    float innerGlow = pow(1.0 - d * 2.0, 5.0) * 1.2;
    
    // Слой 4: Яркое ядро с пульсацией
    float corePulse = sin(uTime * 6.0 + vIndex * 0.3) * 0.3 + 1.0;
    float core = pow(1.0 - d * 2.0, 10.0) * 3.0 * corePulse;
    
    // Слой 5: Сверхъяркий центр
    float hotCenter = pow(1.0 - d * 2.0, 20.0) * 5.0;
    
    // Суммируем все слои bloom
    float bloom = outerGlow + midGlow + innerGlow + core + hotCenter;
    
    // Яркий импульс в голове
    float headBoost = exp(-vIndex * 0.1) * (1.0 + sin(uTime * 4.0) * 0.2);
    
    // HDR-подобное усиление цвета (цвет ярче 1.0)
    vec3 hdrColor = vColor * (3.0 + headBoost * 2.0);
    
    // Добавляем белое свечение в центре для эффекта "блеска"
    vec3 whiteCore = vec3(1.0) * hotCenter * 0.5;
    vec3 finalColor = hdrColor + whiteCore;
    
    // Итоговая альфа
    float alpha = bloom * vOpacity * (1.5 + headBoost);
    
    gl_FragColor = vec4(finalColor, alpha);
  }
`
